// @ts-check

/**
 * @file Renders the task type editor screen.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {Form, FormInputs} from "@kaspernj/api-maker/build/form"
import {useLocalSearchParams, useRouter} from "expo-router"
import {memo, useEffect} from "react"
import PropTypesExact from "prop-types-exact"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {Switch, View} from "react-native-propforge"
import Button from "../../components/button"
import Card from "../../components/card"
import FormTextInput from "../../components/form/form-text-input"
import ProjectsSelect from "../../components/projects/select"
import {Project} from "../../frontend-models/project"
import {TaskType} from "../../frontend-models/task-type"
import errorMessage from "../../shared/error-message"
import {optionalRouteParam} from "../../shared/route-params"
import {useScreenLoading} from "../../components/board-layout/screen-loading"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import Text from "@kaspernj/api-maker/build/utils/text"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TaskTypeEditorForm
 * @property {boolean} defaultForProject Whether this task type is the project default.
 * @property {string | null} id Existing task type id.
 * @property {string} name Editable task type name.
 * @property {string} projectId Selected project id.
 */

/**
 * Builds a route path while preserving system-test params.
 * @param {Record<string, string | string[]>} params Current route params.
 * @param {string} path Destination path.
 * @returns {string} Path with persisted params.
 */
function routePathWithParams(params, path) {
  const nextPath = new URL(path, "http://127.0.0.1")
  const persistedParamNames = [
    "backendHost",
    "backendPort",
    "backendProtocol",
    "systemTest",
    "systemTestClientWsPort",
    "systemTestScoundrelPort",
    "velocious_transport_url"
  ]
  for (const paramName of persistedParamNames) {
    const value = optionalRouteParam(params, paramName, paramName)
    if (value) {
      nextPath.searchParams.set(paramName, value)
    }
  }
  return `${nextPath.pathname}${nextPath.search}${nextPath.hash}`
}

/** @typedef {{[key: string]: unknown}} TaskTypeEditScreenProps */

/**
 * @typedef {object} TaskTypeEditScreenState
 * @property {TaskTypeEditorForm} form Editable task type form.
 * @property {boolean} loading Whether editor data is loading.
 * @property {boolean} saving Whether the task type is saving.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 */

/**
 * Task type editor screen migrated from the legacy Rails/React route.
 * @augments {ShapeComponent<TaskTypeEditScreenProps, TaskTypeEditScreenState>}
 */
class TaskTypeEditScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  form = new FormInputs({})
  /** @type {TaskTypeEditScreenState} */
  state = {
    form: {
      defaultForProject: false,
      id: null,
      name: "",
      projectId: ""
    },
    loading: true,
    saving: false,
    status: null
  }
  loadRequestId = 0
  static propTypes = PropTypesExact({})

  /** Initializes route params and editor loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params =
      /** @type {Record<string, string | string[]>} */ useLocalSearchParams()
    const router = useRouter()

    this.params = params
    this.router = router
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      void this.loadEditor()
    }, [params.id, params.project_id])
  }

  /**
   * Renders the task type editor.
   * @returns {ReactNode} Task type editor content.
   */
  render() {
    const title = this.s.form.id
      ? `${_("Task type")}: ${this.s.form.name || _("Task type")}`
      : _("New task type")
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    if (this.s.loading) {
      return (
        <View
          style={styles.loadingScreen ||= {
            flex: 1
          }}
          testID="taskTypeEditorScreen"
        >
          <Text
            style={styles[`loadingTitle-${themeName}`] ||= {
              fontSize: 24,
              fontWeight: 700,
              marginBottom: 12
            }}
            testID="taskTypeEditorScreen/title"
          >
            {title}
          </Text>
          <Text
            style={styles[`loadingText-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 16
            }}
            testID="taskTypeEditorScreen/loadingText"
          >
            {_("Loading task type...")}
          </Text>
        </View>
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="taskTypeEditorScreen"
      >
        <Text
          style={styles[`title-${themeName}`] ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="taskTypeEditorScreen/title"
        >
          {title}
        </Text>
        <Form form={this.tt.form} onSubmit={this.tt.onSubmit}>
          <Card
            style={styles.formCard ||= {
              gap: 12
            }}
            testID="taskTypeEditorForm"
          >
            <FormTextInput
              defaultValue={this.s.form.name}
              label={_("Name")}
              name="name"
              testID="taskTypeEditorForm/nameInput"
            />
            {!this.s.form.projectId &&
              <Text
                style={styles[`emptyProjects-${themeName}`] ||= {
                  color: colors.textMuted
                }}
                testID="taskTypeEditorForm/noProjectsText"
              >
                {_("No accessible projects available")}
              </Text>
            }
            <ProjectsSelect
              defaultValue={this.s.form.projectId || undefined}
              label={_("Project")}
              name="projectId"
              onChangeValue={this.tt.onProjectChangeValue}
              testID="taskTypeEditorForm/projectInput"
            />
            <View
              style={styles.defaultForProjectRow ||= {
                alignItems: "center",
                flexDirection: "row",
                gap: 8
              }}
              testID="taskTypeEditorForm/defaultForProjectRow"
            >
              <Switch
                onValueChange={this.tt.onDefaultForProjectChange}
                testID="taskTypeEditorForm/defaultForProjectSwitch"
                value={this.s.form.defaultForProject}
              />
              <Text
                style={styles[`defaultForProjectLabel-${themeName}`] ||= {
                  color: colors.text,
                  fontSize: 14
                }}
                testID="taskTypeEditorForm/defaultForProjectLabel"
              >
                {_("Default task type")}
              </Text>
            </View>
            {this.s.status &&
              <Text
                style={styles[`status-${themeName}-${this.s.status.kind}`] ||= {
                  color: this.s.status.kind === "error" ? colors.dangerText : colors.successText,
                  fontWeight: 600,
                  marginBottom: 12,
                  marginTop: 4
                }}
                testID="taskTypeEditorForm/status"
              >
                {this.s.status.text}
              </Text>
            }
            <View
              style={styles.saveRow ||= {
                alignSelf: "flex-start"
              }}
              testID="taskTypeEditorForm/saveRow"
            >
              <Button
                disabled={this.s.saving}
                iconName="floppy-disk"
                label={this.s.saving ? _("Saving...") : _("Save")}
                onPress={this.tt.onSubmit}
                testID="taskTypeEditorForm/saveButton"
                variant="primary"
              />
            </View>
          </Card>
        </Form>
      </View>
    )
  }

  /**
   * Loads the task type editor form data.
   * @returns {Promise<void>} Resolves when editor loading completes.
   */
  async loadEditor() {
    const requestId = ++this.loadRequestId
    try {
      const taskTypeID = optionalRouteParam(this.tt.params, "id", "Task type ID")
      const requestedProjectID = optionalRouteParam(this.tt.params, "project_id", "Project ID")
      const [defaultProject, taskType] = await Promise.all([
        Project
          .query()
          .select(["id", "name"])
          .order([["name", "asc"], ["id", "asc"]])
          .limit(1)
          .first(),
        taskTypeID
          ? TaskType
            .select({
                TaskType: ["defaultForProject", "id", "name", "projectId"]
              })
              .preload("translations")
              .find(taskTypeID)
          : null
      ])
      if (requestId !== this.loadRequestId) return
      const defaultProjectId = defaultProject ? String(defaultProject.id()) : ""
      this.s.form = {
        defaultForProject: taskType ? Boolean(taskType.defaultForProject()) : false,
        id: taskType ? String(taskType.id()) : null,
        name: taskType ? taskType.name() || "" : "",
        projectId: taskType
          ? String(taskType.projectId())
          : requestedProjectID || defaultProjectId
      }
      this.s.loading = false
      this.s.saving = false
      this.s.status = null
    } catch (error) {
      if (requestId !== this.loadRequestId) return
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: `${_("Could not load task type")}: ${errorMessage(error)}`
      }
    }
  }

  /**
   * Applies the default-task-type flag.
   * @param {boolean} defaultForProject Whether the task type should be the project default.
   * @returns {void} Nothing.
   */
  onDefaultForProjectChange = (defaultForProject) => {
    this.s.form = {
      ...this.s.form,
      defaultForProject
    }
    this.s.status = null
  }

  /**
   * Applies the selected project.
   * @param {string} projectID Project id to select.
   * @returns {void} Nothing.
   */
  onProjectChangeValue = (projectID) => {
    this.s.form = {
      ...this.s.form,
      projectId: projectID
    }
    this.s.status = null
  }

  /**
   * Saves the task type form.
   * @returns {Promise<void>} Resolves when save handling completes.
   * @rejects {unknown} When saving fails.
   */
  onSubmit = async () => {
    const values = /** @type {Record<string, string>} */ (this.tt.form.asObject())
    this.s.saving = true
    this.s.status = null
    try {
      /** @type {InstanceType<typeof TaskType>} */
      let taskType
      if (this.s.form.id) {
        taskType = await TaskType.find(this.s.form.id)
      } else {
        taskType = new TaskType()
      }
      taskType.setName(values.name || "")
      taskType.setProjectId(String(values.projectId))
      taskType.setDefaultForProject(this.s.form.defaultForProject)
      await taskType.save()
      this.tt.router.push(
        routePathWithParams(
          this.tt.params,
          `/projects/${values.projectId}/task-types`
        )
      )
    } catch (error) {
      this.s.saving = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
      throw error
    }
  }
}

/** @type {NamedExoticComponent<TaskTypeEditScreenProps>} */
const TaskTypeEditScreenComponent = memo(shapeComponent(TaskTypeEditScreen))

export default TaskTypeEditScreenComponent
